import React, { useState } from 'react';
import { Link } from 'react-router-dom';

import { PageHeader } from '../../components/ui/PageHeader/PageHeader';
import { Tabs, type TabItem } from '../../components/ui/Tabs/Tabs';
import { SearchField } from '../../components/ui/SearchField/SearchField';
import { EmptyState } from '../../components/ui/EmptyState/EmptyState';
import { TableFooter } from '../../components/ui/TableFooter/TableFooter';
import { DataTable } from '../../components/ui/DataTable/DataTable';
import { useDebouncedValue } from '../../hooks/useDebouncedValue';
import { useGetBlockedDomainsQuery, useGetBlockedSourcesQuery } from '../../entities/blocks/api/blocksApi';
import { DEFAULT_PAGE_SIZE } from '../../shared/config/pagination';
import { formatRangeLabel } from '../../utils/format';
import { formatNumber } from '../../utils/number';
import { FilterBar, FilterSearch } from '../../components/ui/FilterBar/FilterBar';
import styles from './BlocksPage.module.scss';

type BlocksTab = 'domains' | 'sources';

const TABS: TabItem<BlocksTab>[] = [
    { key: 'domains', label: 'Управление глобальными блокировками' },
    { key: 'sources', label: 'Заблокированные источники на проектах' },
];

const formatDate = (date: Date | null) =>
    date ? date.toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '—';

export function BlocksPage() {
    const [tab, setTab] = useState<BlocksTab>('domains');
    const [page, setPage] = useState(1);
    const [search, setSearch] = useState('');
    const query = useDebouncedValue(search, 400);

    const args = { pageNum: page, pageSize: DEFAULT_PAGE_SIZE, query };
    const domains = useGetBlockedDomainsQuery(args, { skip: tab !== 'domains' });
    const sources = useGetBlockedSourcesQuery(args, { skip: tab !== 'sources' });

    const active = tab === 'domains' ? domains : sources;
    const pageCount = Math.max(1, active.data?.pages ?? 1);
    const total = active.data?.total ?? 0;
    const isEmpty = (active.data?.items.length ?? 0) === 0;

    const changePage = (next: number) => setPage(Math.min(Math.max(1, next), pageCount));

    const changeSearch = (value: string) => {
        setSearch(value);
        setPage(1);
    };

    const changeTab = (next: BlocksTab) => {
        setTab(next);
        setPage(1);
        setSearch('');
    };

    const emptyState = (() => {
        if (active.isError) {
            return (
                <EmptyState
                    title="Не удалось загрузить список"
                    description="Проверьте соединение и попробуйте ещё раз."
                    action={{ label: 'Повторить', onClick: () => active.refetch() }}
                />
            );
        }

        if (isEmpty) {
            return (
                <EmptyState
                    title="Ничего не найдено"
                    description={
                        search
                            ? 'Проверьте написание или очистите поиск.'
                            : 'Заблокированных записей пока нет.'
                    }
                    action={search ? { label: 'Очистить поиск', onClick: () => changeSearch('') } : undefined}
                />
            );
        }

        return undefined;
    })();

    return (
        <div className={styles.page}>
            <PageHeader
                title="Блокировки"
                meta={active.data ? [<>Всего <b>{formatNumber(total)}</b></>] : undefined}
                className={styles.header}
            />

            <Tabs items={TABS} active={tab} onChange={changeTab} />

            <section className={styles.pane} role="tabpanel" aria-label={TABS.find((t) => t.key === tab)?.label}>
                <FilterBar>
                    <FilterSearch
                        value={search}
                        placeholder={tab === 'domains' ? 'Домен или адрес' : 'Источник, товар или проект'}
                        onChange={changeSearch}
                    />
                </FilterBar>

                <DataTable
                    scrollKey={`${tab}-${page}-${query}`}
                    loading={active.isFetching}
                    isLoading={active.isLoading}
                    loadingText="Загружаем список…"
                    empty={emptyState}
                    footer={
                        total > 0 ? (
                            <TableFooter
                                page={page}
                                totalPages={pageCount}
                                rangeLabel={formatRangeLabel(page, DEFAULT_PAGE_SIZE, total)}
                                onPageChange={changePage}
                            />
                        ) : undefined
                    }
                >
                    {tab === 'domains' ? (
                        <>
                            <colgroup>
                                <col className={styles.colSource} />
                                <col />
                                <col className={styles.colAuthor} />
                                <col className={styles.colDate} />
                            </colgroup>

                            <thead>
                                <tr>
                                    <th scope="col">Источник</th>
                                    <th scope="col">Причина</th>
                                    <th scope="col">Кто заблокировал</th>
                                    <th scope="col">Дата</th>
                                </tr>
                            </thead>

                            <tbody>
                                {domains.data?.items.map((item) => (
                                    <tr key={item.id}>
                                        <td>
                                            <span className={styles.domain}>{item.domain}</span>
                                            {item.url && (
                                                <a
                                                    className={styles.url}
                                                    href={item.url}
                                                    target="_blank"
                                                    rel="noreferrer noopener"
                                                >
                                                    {item.url}
                                                </a>
                                            )}
                                        </td>
                                        <td>
                                            <span className={styles.reason} title={item.reason}>
                                                {item.reason}
                                            </span>
                                        </td>
                                        <td className={styles.muted}>{item.author}</td>
                                        <td className={styles.cellNums}>{formatDate(item.createdAt)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </>
                    ) : (
                        <>
                            <colgroup>
                                <col className={styles.colSource} />
                                <col className={styles.colProduct} />
                                <col className={styles.colProject} />
                                <col />
                                <col className={styles.colAuthor} />
                            </colgroup>

                            <thead>
                                <tr>
                                    <th scope="col">Источник</th>
                                    <th scope="col">Товар</th>
                                    <th scope="col">Проект</th>
                                    <th scope="col">Причина</th>
                                    <th scope="col">Кто и когда</th>
                                </tr>
                            </thead>

                            <tbody>
                                {sources.data?.items.map((item) => (
                                    <tr key={item.key}>
                                        <td>
                                            <span className={styles.domain}>{item.host}</span>
                                            <a
                                                className={styles.url}
                                                href={item.fullUrl}
                                                target="_blank"
                                                rel="noreferrer noopener"
                                                title={item.sourceTitle}
                                            >
                                                {item.sourceTitle}
                                            </a>
                                        </td>
                                        <td>
                                            <span className={styles.clamp} title={item.productName}>
                                                {item.productName}
                                            </span>
                                        </td>
                                        <td>
                                            {/* Внутренняя ссылка вместо абсолютной из project_url:
                                                иначе из теста уводило бы на боевой домен. */}
                                            {item.projectId ? (
                                                <Link to={`/projects/${item.projectId}`} className={styles.link}>
                                                    {item.projectName}
                                                </Link>
                                            ) : (
                                                item.projectName
                                            )}
                                        </td>
                                        <td>
                                            <span className={styles.reason} title={item.reason}>
                                                {item.reason}
                                            </span>
                                        </td>
                                        <td>
                                            <span className={styles.muted}>{item.blockedBy}</span>
                                            <small className={styles.sub}>{formatDate(item.blockedAt)}</small>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </>
                    )}
                </DataTable>
            </section>
        </div>
    );
}